import { Dialog } from "primereact/dialog";
import { Fade } from "@material-ui/core";
import { Calendar } from "primereact/calendar";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import api from "../../services/api";
import { dateToHour } from "../../utils/masks";

export default function ServiceOrderReportsAttendance({
  visible = true,
  onHide = () => {},
  serviceOrderId,
  onSubmit
}) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingReportId, setEditingReportId] = useState(null);
  const [editingDateReportId, setEditingDateReportId] = useState(null);
  const [startAttendance, setStartAttendance] = useState(null);
  const [endAttendance, setEndAttendance] = useState(null);
  const [editingDate, setEditingDate] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const toast = useRef(null);

  const parseTime = (timeString, fallback = "00:00") =>
    timeString
      ? new Date(`1970-01-01T${timeString?.trim()}:00`)
      : new Date(`1970-01-01T${fallback}:00`);

  const getReports = useCallback(async () => {
    if (!serviceOrderId) {
      toast.current?.show({
        severity: "warn",
        summary: "Aviso",
        detail: "ID da Ordem de Serviço não informado.",
        life: 3000
      });
      return;
    }

    setLoading(true);
    try {
      // Usar o endpoint correto: GET /api/service_order/report/list
      const response = await api.get('/service_order/report/list', {
        params: {
          service_order_id: serviceOrderId,
          rows: 100 // Buscar até 100 reports para esta OS
        }
      });
      
      const loadedReports = response.data?.data || [];
      setReports(loadedReports);
      setDataLoaded(true);
      
      if (loadedReports.length === 0) {
        toast.current?.show({
          severity: "info",
          summary: "Informação",
          detail: "Nenhum relatório encontrado para esta OS.",
          life: 3000
        });
      }
    } catch (error) {
      console.error("Erro ao buscar service_orders_report:", error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || "Erro ao carregar registros. Verifique se existem registros para esta OS.";
      
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: errorMessage,
        life: 5000
      });
      setReports([]);
      setDataLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [serviceOrderId]);

  // Carregar dados automaticamente quando o modal abrir
  useEffect(() => {
    if (visible && serviceOrderId && !dataLoaded) {
      getReports();
    }
  }, [visible, serviceOrderId, dataLoaded, getReports]);

  // Resetar dados quando o modal fecha
  useEffect(() => {
    if (!visible) {
      setDataLoaded(false);
      setReports([]);
      setEditingReportId(null);
      setEditingDateReportId(null);
      setStartAttendance(null);
      setEndAttendance(null);
      setEditingDate(null);
    }
  }, [visible]);

  const handleEditClick = (report) => {
    if (editingDateReportId === report.id) {
      setEditingDateReportId(null);
      setEditingDate(null);
    }
    setEditingReportId(report.id);
    const attendanceTime = report.attendance_time || "";
    const attendanceArray = attendanceTime.split("|");
    setStartAttendance(parseTime(attendanceArray[0]));
    setEndAttendance(parseTime(attendanceArray[1] || "00:00"));
  };

  const handleSave = async (reportId) => {
    if (!startAttendance || !endAttendance) {
      return;
    }
    const startTime = dateToHour(startAttendance);
    const endTime = dateToHour(endAttendance);
    // Formato correto: "HH:MM|HH:MM"
    const attendanceTime = `${startTime}|${endTime}`;

    try {
      // Usar o novo endpoint: PUT /api/service_order/report/update/{reportId}
      const endpoint = `/service_order/report/update/${reportId}`;
      await api.put(endpoint, {
        attendance_time: attendanceTime
      });

      setEditingReportId(null);
      setStartAttendance(null);
      setEndAttendance(null);

      // Recarregar a lista para garantir sincronização
      await getReports();

      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: "Horário atualizado com sucesso!",
        life: 3000
      });

      if (onSubmit) {
        onSubmit();
      }
    } catch (error) {
      console.error("Erro ao atualizar horário:", error);
      const errorMessage = error.response?.data?.error || "Erro ao atualizar horário. Tente novamente.";
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: errorMessage,
        life: 3000
      });
    }
  };

  const handleCancel = () => {
    setEditingReportId(null);
    setStartAttendance(null);
    setEndAttendance(null);
  };

  const handleEditDateClick = (report) => {
    if (editingReportId === report.id) {
      setEditingReportId(null);
      setStartAttendance(null);
      setEndAttendance(null);
    }
    setEditingDateReportId(report.id);
    const date = report.created_at;
    setEditingDate(date ? new Date(date) : new Date());
  };

  const handleSaveDate = async (reportId) => {
    if (!editingDate) {
      return;
    }

    if (isNaN(editingDate.getTime())) {
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Data inválida. Por favor, selecione uma data válida.",
        life: 3000
      });
      return;
    }

    try {
      const year = editingDate.getFullYear();
      const month = String(editingDate.getMonth() + 1).padStart(2, '0');
      const day = String(editingDate.getDate()).padStart(2, '0');
      const hours = String(editingDate.getHours()).padStart(2, '0');
      const minutes = String(editingDate.getMinutes()).padStart(2, '0');
      const seconds = String(editingDate.getSeconds()).padStart(2, '0');
      
      const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

      const endpoint = `/service_order/report/update/${reportId}`;
      await api.put(endpoint, {
        created_at: formattedDate
      });

      setEditingDateReportId(null);
      setEditingDate(null);

      // Recarregar a lista para garantir sincronização
      await getReports();

      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: "Data atualizada com sucesso!",
        life: 3000
      });

      if (onSubmit) {
        onSubmit();
      }
    } catch (error) {
      console.error("Erro ao atualizar data:", error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || "Erro ao atualizar data. Tente novamente.";
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: errorMessage,
        life: 3000
      });
    }
  };

  const handleCancelDate = () => {
    setEditingDateReportId(null);
    setEditingDate(null);
  };

  const renderDate = (rowData) => {
    if (editingDateReportId === rowData.id) {
      return (
        <div className="d-flex flex-column gap-2">
          <Calendar
            value={editingDate}
            onChange={(e) => setEditingDate(e.value)}
            dateFormat="dd/mm/yy"
            showIcon
            showTime
            hourFormat="24"
            style={{ width: "200px" }}
          />
          <div className="d-flex gap-2">
            <Button
              label="Salvar"
              icon="pi pi-check"
              size="small"
              onClick={() => handleSaveDate(rowData.id)}
            />
            <Button
              label="Cancelar"
              icon="pi pi-times"
              size="small"
              className="p-button-text"
              onClick={handleCancelDate}
            />
          </div>
        </div>
      );
    }

    const date = rowData.created_at;
    const displayDate = date
      ? new Date(date).toLocaleDateString("pt-BR")
      : "-";

    return (
      <div className="d-flex align-items-center gap-2">
        <span>{displayDate}</span>
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-sm"
          onClick={() => handleEditDateClick(rowData)}
          disabled={editingReportId === rowData.id}
          tooltip={editingReportId === rowData.id ? "Finalize a edição do tempo primeiro" : "Editar data"}
        />
      </div>
    );
  };

  const renderAttendance = (rowData) => {
    if (editingReportId === rowData.id) {
      return (
        <div className="d-flex flex-column gap-2">
          <div className="d-flex gap-2 align-items-center">
            <Calendar
              value={startAttendance}
              onChange={(e) => setStartAttendance(e.value)}
              timeOnly
              hourFormat="24"
              style={{ width: "120px" }}
            />
            <span>-</span>
            <Calendar
              value={endAttendance}
              onChange={(e) => setEndAttendance(e.value)}
              timeOnly
              hourFormat="24"
              style={{ width: "120px" }}
            />
          </div>
          <div className="d-flex gap-2">
            <Button
              label="Salvar"
              icon="pi pi-check"
              size="small"
              onClick={() => handleSave(rowData.id)}
            />
            <Button
              label="Cancelar"
              icon="pi pi-times"
              size="small"
              className="p-button-text"
              onClick={handleCancel}
            />
          </div>
        </div>
      );
    }

    const attendanceTime = rowData.attendance_time || "";
    if (!attendanceTime) {
      return <span>-</span>;
    }

    // Formatar exibição: substituir | por - para melhor visualização
    const displayTime = attendanceTime.replace("|", " - ");

    return (
      <div className="d-flex align-items-center gap-2">
        <span>{displayTime}</span>
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-sm"
          onClick={() => handleEditClick(rowData)}
          disabled={editingDateReportId === rowData.id}
          tooltip={editingDateReportId === rowData.id ? "Finalize a edição da data primeiro" : "Editar tempo"}
        />
      </div>
    );
  };

  const footer = (
    <>
      <Button
        label="Fechar"
        icon="pi pi-times"
        className="p-button-text"
        onClick={onHide}
      />
    </>
  );

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        visible={visible}
        style={{ marginTop: 60, width: "90rem", maxWidth: "95vw" }}
        className="p-fluid"
        modal
        onHide={onHide}
        header={`Editar horários de atendimento dos relatórios - OS ${serviceOrderId ? `#${serviceOrderId}` : ""}`}
        footer={footer}
      >
        <Fade in={true}>
          <div className="container pt-2">
            {loading && (
              <div className="d-flex flex-column align-items-center justify-content-center py-5">
                <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', marginBottom: '1rem' }}></i>
                <p>Carregando relatórios...</p>
              </div>
            )}

            {!loading && dataLoaded && reports.length === 0 && (
              <div className="alert alert-info mb-3">
                <strong>Nenhum relatório encontrado</strong>
                <p className="mb-0">
                  Não há relatórios criados para esta Ordem de Serviço.
                </p>
              </div>
            )}

            {!loading && dataLoaded && reports.length > 0 && (
              <>
                <div className="alert alert-success mb-3">
                  <strong>{reports.length}</strong> relatório(s) encontrado(s) para esta OS
                </div>
                <DataTable
                  value={reports}
                  loading={loading}
                  emptyMessage="Nenhum relatório encontrado"
                  className="p-datatable-sm"
                >
                  <Column
                    field="date"
                    header="Data"
                    body={renderDate}
                    style={{ minWidth: "250px" }}
                  />
                  <Column
                    field="attendance"
                    header="Tempo"
                    body={renderAttendance}
                    style={{ minWidth: "200px" }}
                  />
                  <Column
                    field="created_by_name"
                    header="Técnico"
                    body={(rowData) => rowData.created_by?.name || "-"}
                    style={{ minWidth: "150px" }}
                  />
                  <Column
                    field="service_description"
                    header="Descrição dos Serviços a Serem Executados"
                    body={(rowData) => rowData.service_description || "-"}
                    style={{ minWidth: "300px" }}
                  />
                </DataTable>
              </>
            )}
          </div>
        </Fade>
      </Dialog>
    </>
  );
}

